import { IoChevronDown, IoClose } from 'react-icons/io5'
import { Field, Flex, Icon, Spinner } from '@chakra-ui/react'
import { isEmpty } from 'lodash'
import { forwardRef, useCallback } from 'react'
import ReactSelect from 'react-select'
import { FormLabel } from '../Form/FormLabel'
import { useColorMode } from 'uikit/ColorMode/ColorMode'

export const Select = forwardRef(
  (
    {
      name,
      value,
      options = [],
      isMulti = false,
      isInvalid = false,
      endAddon = undefined,
      label = '',
      isForm = false,
      onChange,
      ...props
    },
    ref
  ) => {
    const { colorMode } = useColorMode()
    const themeBgControl = colorMode === 'light' ? '#ffffff' : 'inherit'
    const themeColorSingleValue = colorMode === 'light' ? '#000000' : '#ffffff'
    const themeControlBorderColor = colorMode === 'light' ? '#E2E8F0' : 'rgba(255, 255, 255, 0.16)'
    const themeControlBorderColorFocused = colorMode === 'light' ? '#3182ce' : '#63b3ed'
    const themeControlBoxShadowFocused =
      colorMode === 'light' ? '0 0 0 1px #3182ce' : '0 0 0 1px #63b3ed'
    const themeControlBorderColorHoverFocused = colorMode === 'light' ? '#3182ce' : '#63b3ed'
    const themeControlBorderColorHover =
      colorMode === 'light' ? '#CBD5E0' : 'rgba(255, 255, 255, 0.24)'
    const themeBgMenuList = colorMode === 'light' ? '#ffffff' : '#2D3748'
    const themeBgOptionSelected = colorMode === 'light' ? '#3182ce' : '#90cdf4'
    const themeBgOptionHoverSelected = colorMode === 'light' ? '#3182ce' : '#90cdf4'
    const themeBgOptionHover = colorMode === 'light' ? '#EDF2F6' : 'rgba(255, 255, 255, 0.06)'
    const themeColorOptionActiveSelected = colorMode === 'light' ? '#ffffff' : '#000000'
    const themeColorOptionActive = colorMode === 'light' ? '#3182ce' : 'rgba(255, 255, 255, 0.80)'
    const themeColorOption = colorMode === 'light' ? '#ffffff' : '#000000'
    const themeColorOptionSelected = colorMode === 'light' ? '#000000' : '#ffffff'
    const themePlaceholderColor = colorMode === 'light' ? '#A0AEC0' : 'rgba(255, 255, 255, 0.24)'
    const themeColorNoOptionsMessage =
      colorMode === 'light' ? '#A0AEC0' : 'rgba(255, 255, 255, 0.24)'
    const themeBgMultiValue = colorMode === 'light' ? '#EDF2F7' : '#2D3748'
    const themeColorMultiValueLabel = colorMode === 'light' ? '#000000' : '#fffff'
    const themeColorInput = colorMode === 'light' ? '#000000' : '#ffffff'
    const themeColorInvalid = colorMode === 'light' ? '#e53e3e' : '#FC8181'
    const themeShadowInvalid = colorMode === 'light' ? '0 0 0 1px #e53e3e' : '0 0 0 1px #FC8181'

    const indicatorSeparatorStyle = {
      alignSelf: 'stretch',
      backgroundColor: '#fff',
      marginBottom: 8,
      marginTop: 8,
      width: 0,
    }

    const IndicatorSeparator = ({ innerProps }) => {
      return <span style={indicatorSeparatorStyle} {...innerProps} />
    }

    const LoadingIndicator = () => {
      return <Spinner color="blue.500" size="xs" />
    }

    const DropdownIndicator = ({ innerProps }) => {
      return (
        <Flex alignItems="center" alignContent="center" cursor="pointer" {...innerProps}>
          <Icon as={IoChevronDown} fontSize="20px" />
        </Flex>
      )
    }

    const ClearIndicator = ({ innerProps }) => {
      return (
        <Flex
          alignItems="center"
          alignContent="center"
          width="20px"
          cursor="pointer"
          {...innerProps}
        >
          <IoClose size="20px" />
        </Flex>
      )
    }

    const LoadingMessage = (config) => (
      <div {...config.innerProps} style={config.getStyles('loadingMessage', config)}>
        Загрузка...
      </div>
    )

    const customStyles = {
      control: (provided, state) => {
        const { isFocused } = state
        const { isSearchable } = state.selectProps
        return {
          ...provided,
          borderColor: isInvalid
            ? themeColorInvalid
            : isFocused
              ? themeControlBorderColorFocused
              : themeControlBorderColor,
          boxShadow: isInvalid ? themeShadowInvalid : isFocused && themeControlBoxShadowFocused,
          padding: isSearchable
            ? '0.063rem 0.5rem 0.063rem 0.34rem'
            : '0.313rem 0.5rem 0.313rem 0.3rem',
          backgroundColor: themeBgControl,
          transition: '0.3s',
          borderRadius: '0.375rem',
          '&:hover': {
            borderColor: isInvalid
              ? themeColorInvalid
              : isFocused
                ? themeControlBorderColorHoverFocused
                : themeControlBorderColorHover,
            boxShadow: isInvalid && '0 0 0 1px #fffff',
          },
        }
      },
      menu: (provided) => ({
        ...provided,
        backgroundColor: 'inherit',
        zIndex: 999,
        borderWidth: '1px',
        //borderColor: '#E2E8F0',
        borderRadius: '0.375rem',
        boxShadow: '0 1px 3px 0 rgba(0,0,0,0.1), 0 1px 2px 0 rgba(0,0,0,0.06)',
      }),
      menuList: (provided) => ({
        ...provided,
        backgroundColor: themeBgMenuList,
        transition: 'background-color 220ms,color 220ms',
        borderRadius: '0.25rem',
      }),
      option: (provided, state) => {
        const { isSelected } = state
        return {
          ...provided,
          backgroundColor: isSelected && themeBgOptionSelected,
          color: isSelected ? themeColorOption : themeColorOptionSelected,
          ':active': {
            color: isSelected ? themeColorOptionActiveSelected : themeColorOptionActive,
          },
          ':hover': {
            ...provided[':hover'],
            backgroundColor: isSelected ? themeBgOptionHoverSelected : themeBgOptionHover,
          },
        }
      },
      multiValue: (provided) => ({
        ...provided,
        backgroundColor: themeBgMultiValue,
        maxWidth: '230px',
      }),
      multiValueLabel: (base) => ({
        ...base,
        color: themeColorMultiValueLabel,
      }),
      dropdownIndicator: (provided) => ({
        ...provided,
        color: '#1A202C',
        ':hover': '#1A202C',
      }),
      clearIndicator: (provided) => ({
        ...provided,
        color: '#1A202C',
        ':hover': '#1A202C',
      }),
      singleValue: (provided, state) => {
        const opacity = state.isDisabled ? 0.4 : 1
        const transition = 'opacity 300ms'

        return { ...provided, opacity, transition, color: themeColorSingleValue }
      },
      placeholder: (base) => ({
        ...base,
        fontSize: '1em',
        color: themePlaceholderColor,
      }),
      noOptionsMessage: (base) => {
        return {
          ...base,
          color: themeColorNoOptionsMessage,
        }
      },
      input: (base) => ({
        ...base,
        fontSize: '1em',
        color: themeColorInput,
      }),
    }
    const customTheme = (theme) => ({
      ...theme,
      borderRadius: 4,
    })

    const getValue = () => {
      if (isForm) {
        return value
      }
      if (!isMulti) {
        const optionValue = options.find((item) => item.value === value)
        return !isEmpty(optionValue) ? optionValue : null
      } else {
        return value
      }
    }

    const onChangeHandle = useCallback(
      (item) => {
        if (item === null) {
          onChange({
            ...item,
            value: '',
          })
        } else {
          onChange(item)
        }
      },
      [onChange]
    )

    return (
      <Field.Root display="flex" flexDirection="column" alignItems="stretch" invalid={isInvalid}>
        {label && <FormLabel>{label}</FormLabel>}
        <ReactSelect
          ref={ref}
          name={name}
          isMulti={isMulti}
          options={options}
          styles={customStyles}
          components={{
            IndicatorSeparator,
            LoadingIndicator,
            LoadingMessage,
            DropdownIndicator,
            ClearIndicator,
          }}
          noOptionsMessage={() => 'Нет опций'}
          theme={customTheme}
          value={getValue()}
          onChange={onChangeHandle}
          {...props}
          isDisabled={props.disabled}
        />
      </Field.Root>
    )
  }
)
